/**
 * Supported UI languages — the single source of truth for the whole repo.
 *
 * Deliberately a pure leaf module with no Convex or server-only imports, in the
 * same spirit as `logic/portal/portal_host.ts`: it is consumed by BOTH the
 * Convex backend (the `users.ui_language` schema validator) and the Next.js app
 * (locale resolution, middleware, the language switcher), so the list cannot
 * drift between the two.
 *
 * Adding a language is the LAST step of adding a language: fill its message
 * catalog via `scripts/i18n/translate.ts` first, then extend this list. The
 * completeness gate in `scripts/check-i18n.mjs` reads exactly this array and
 * will fail every missing key the moment a locale appears here.
 */

export const SUPPORTED_LOCALES = ["de", "en"] as const;

export type Locale = (typeof SUPPORTED_LOCALES)[number];

/** German is the source language — every message is authored in `de` first. */
export const DEFAULT_LOCALE: Locale = "de";

export function isSupportedLocale(value: unknown): value is Locale {
  return (
    typeof value === "string" &&
    (SUPPORTED_LOCALES as readonly string[]).includes(value)
  );
}

/**
 * Narrows arbitrary input to a supported locale, falling back to the default.
 * Use at every boundary where a locale arrives as a plain string: cookie,
 * request header, database column, query parameter.
 */
export function toLocale(value: unknown): Locale {
  return isSupportedLocale(value) ? value : DEFAULT_LOCALE;
}
